import {
  Box,
  Flex,
  Heading,
  Input,
  Label,
  Switch,
  Textarea,
  TextButton,
  ValidationMessage,
} from '@modusign/design-system';
import { Controller, useFormContext } from 'react-hook-form-v7';

import { AttachmentSettingFormValues } from '@app/features/apply-react-signing/components/FieldSetupStep/ParticipantFieldStatusBoard/AttachmentSettingModal';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';

interface AttachmentRequestCardProps {
  id: string;
  showDataLabelInput: boolean;
  index: number;
  othersDataLabels: string[];
  onDeleteClick: (id: string) => void;
}

const defaultProps = {
  onDeleteClick() {},
};

function AttachmentRequestCard({
  id,
  index,
  showDataLabelInput,
  othersDataLabels,
  onDeleteClick,
}: AttachmentRequestCardProps) {
  const {
    control,
    formState: { errors },
    register,
    watch,
  } = useFormContext<AttachmentSettingFormValues>();

  const curFormState = watch();
  const myDataLabels = Object.keys(curFormState)
    .filter((key) => key !== id)
    .map((key) => curFormState[key]?.dataLabel?.trim())
    .filter((dataLabel) => !!dataLabel) as string[];
  const allDataLabels = othersDataLabels.concat(...myDataLabels);

  return (
    <Box
      backgroundColor="formBackgroundNeutralSlightlyDark"
      borderColor="formBorderNeutralBase"
      borderRadius="6px"
      borderStyle="solid"
      borderWidth="1px"
    >
      <Flex
        justifyContent="space-between"
        px={7}
        py={5}
        sx={{
          borderBottomColor: 'formBorderNeutralBase',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
        }}
      >
        <Heading color="textNeutralLight" fontSize={2}>{`파일 요청 ${index + 1}`}</Heading>
        <TextButton onClick={() => onDeleteClick(id)}>
          <Box
            as="i"
            className="fa fa-trash"
            sx={{
              marginRight: '3px',
            }}
          />
          삭제
        </TextButton>
      </Flex>
      <Flex flexDirection="column" gap="20px" px={7} py={5}>
        <Flex>
          <Label
            color="textNeutralSlightlyLight"
            htmlFor={`AttachmentRequestCard-${index}-attachment-type`}
            mb={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: '0 0 110px',
            }}
          >
            파일 종류
          </Label>
          <Box flex={1}>
            <Input
              id={`AttachmentRequestCard-${index}-attachment-type`}
              isInvalid={!!errors[id]?.attachmentType}
              maxLength={31}
              placeholder="30자 이하로 입력 (예: 통장사본)"
              {...register(`${id}.attachmentType`, {
                maxLength: { value: 30, message: '30자 이하로 입력해 주세요.' },
                required: '30자 이하로 입력해 주세요.',
                pattern: {
                  value: /^[^/?<>\\":*|₩]+$/,
                  message: '/?<>\\":*|₩은 입력할 수 없습니다.',
                },
              })}
              type="text"
            />
            {errors[id]?.attachmentType?.message && (
              <ValidationMessage>{errors[id]?.attachmentType?.message}</ValidationMessage>
            )}
          </Box>
        </Flex>
        <Flex>
          <Label
            color="textNeutralSlightlyLight"
            htmlFor={`AttachmentRequestCard-${index}-message`}
            mt={3}
            sx={{
              flex: '0 0 110px',
            }}
          >
            추가 안내 사항
          </Label>
          <Box flex={1}>
            <Textarea
              id={`AttachmentRequestCard-${index}-message`}
              maxLength={100}
              placeholder="100자 이하로 입력 (예: 개인사업자의 경우에 한합니다.)"
              width={1}
              {...register(`${id}.message`)}
            />
          </Box>
        </Flex>
        <Flex>
          <Label
            color="textNeutralSlightlyLight"
            htmlFor={`AttachmentRequestCard-${index}-required`}
            mb={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: '0 0 110px',
            }}
          >
            필수 여부
          </Label>
          <Box width="35px">
            <Controller
              control={control}
              defaultValue
              name={`${id}.required`}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  id={`AttachmentRequestCard-${index}-required`}
                  name="required"
                  onClick={() => onChange(!value)}
                />
              )}
            />
          </Box>
        </Flex>
        {showDataLabelInput && (
          <Flex>
            <Label
              color="textNeutralSlightlyLight"
              htmlFor={`AttachmentRequestCard-${index}-data-label`}
              mb={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 0 110px',
              }}
            >
              데이터 라벨
            </Label>
            <Box flex={1}>
              <Input
                defaultValue={createDataLabel()}
                id={`AttachmentRequestCard-${index}-data-label`}
                isInvalid={!!errors[id]?.dataLabel}
                maxLength={100}
                placeholder="100자 이하로 입력"
                {...register(`${id}.dataLabel`, {
                  validate: {
                    required: (value = '') => !!value.trim() || '데이터 라벨 입력은 필수입니다.',
                    duplicate: (value = '') =>
                      !allDataLabels.includes(value.trim()) ||
                      '다른 첨부파일 요청과 중복된 데이터 라벨입니다.',
                  },
                })}
                type="text"
              />
              {errors[id]?.dataLabel?.message && (
                <ValidationMessage>{errors[id]?.dataLabel?.message}</ValidationMessage>
              )}
            </Box>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}

AttachmentRequestCard.defaultProps = defaultProps;

export default AttachmentRequestCard;
